'use client'

import { useEffect, useState, type ReactNode } from 'react'
import Overview from '@/components/ui/Overview'
import Sheet from '@/components/ui/Sheet'
import Link from '@/components/ui/Link'
import { cn } from '@/lib/utils'
import { Icon } from '@/icons/Icon'
import { useDeferredActivation } from '@/hooks/useDeferredActivation'

export type RankChange = {
  updatedAt: string
  // Concrete previous rank — when present the indicator shows the from→to magnitude.
  previous?: number
  // Explicit direction for magnitude-less overrides (arrow only, no number).
  direction?: 'up' | 'down'
}

// How recent a change must be (in days) to read as a concrete "Rank up/down" with a
// "Changed:" date. Older changes read as "Trending up/down" with a "Since" date.
const RANK_DELTA_RECENT_DAYS = 90

function isRankUp(rank: number, rankChange: RankChange): boolean {
  if (typeof rankChange.previous === 'number') return rankChange.previous > rank
  return rankChange.direction === 'up'
}

type RankIndicatorProps = {
  // null when there is no concrete rank to show (e.g. the app isn't on the
  // category's main ranked list) — renders a dash instead of a number.
  rank: number | null
  rankChange?: RankChange
  theme?: 'light' | 'dark'
  onClickAction?: () => void
  // When set, the pill (outside the mobile Sheet trigger, which always opens the
  // trend detail sheet) renders as a real link instead of a button.
  href?: string
  // 'vertical' (default): a tall pill with the trend chevron stacked above/below the
  // rank number. 'horizontal': a short wide pill with the chevron to the left of the
  // rank number — for rows where the indicator sits inline with other elements.
  orientation?: 'vertical' | 'horizontal'
}

export default function RankIndicator({
  rank,
  rankChange,
  theme,
  onClickAction,
  href,
  orientation = 'vertical',
}: RankIndicatorProps) {
  const [showSheet, setShowSheet] = useState(false)
  const isUp = rankChange && rank !== null ? isRankUp(rank, rankChange) : false

  // The mobile Sheet's open state is plain React state — the responsive *choice*
  // (sheet vs hover overview) is handled by CSS below, not a breakpoint hook. The
  // ONE thing CSS can't do is dismiss an already-open portal modal when the
  // viewport grows past `sm`: the Sheet's overlay is a fixed-position portal to
  // <body>, so it escapes the `sm:hidden` wrapper and would linger on desktop.
  // Guard only that, with a matchMedia listener mounted *only while the sheet is
  // open* (zero idle listeners), closing it on the crossover. This is modal
  // lifecycle, not layout detection — which is why CSS alone can't cover it.
  useEffect(() => {
    if (!showSheet) return
    const desktop = window.matchMedia('(min-width: 640px)')
    if (desktop.matches) {
      setShowSheet(false)
      return
    }
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setShowSheet(false)
    }
    desktop.addEventListener('change', handleChange)
    return () => desktop.removeEventListener('change', handleChange)
  }, [showSheet])

  function renderPill({ onClick, href: pillHref }: { onClick?: () => void; href?: string } = {}) {
    const isHorizontal = orientation === 'horizontal'
    const changeColorClasses = rankChange
      ? isUp
        ? 'bg-green-500/10 text-green-500 transition-colors duration-300 hover:bg-green-500/20'
        : 'bg-red-500/15 text-red-500 transition-colors duration-300 hover:bg-red-500/30'
      : undefined

    const pillClassName = cn(
      'relative flex shrink-0 cursor-pointer rounded-full',
      isHorizontal
        ? cn('h-7 items-center', rankChange && 'gap-[3px] pl-2.5', changeColorClasses)
        : cn(
            'h-[38px] w-5 justify-center sm:h-12 sm:w-7',
            rankChange
              ? cn('items-start', isUp ? 'items-end' : 'items-start', changeColorClasses)
              : 'sm:items-center',
          ),
    )

    const content = (
      <>
        {rankChange && (
          <div
            className={cn(
              'flex',
              isHorizontal
                ? 'shrink-0'
                : cn(
                    'absolute left-1/2 -translate-x-1/2',
                    isUp ? 'top-1 sm:top-1.5' : 'bottom-1 sm:bottom-1.5',
                  ),
            )}
          >
            <Icon
              name="chevron-left"
              size={12}
              className={cn('scale-90 sm:scale-100', isUp ? 'rotate-90' : '-rotate-90')}
            />
          </div>
        )}
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full border font-mono text-12 leading-none',
            theme === 'dark'
              ? 'border-white/15 bg-black text-white'
              : 'border-gray-200 bg-white text-gray-600 dark:border-white/15 dark:bg-black dark:text-white',
            isHorizontal ? 'h-7 px-2.5' : 'h-5 w-5 sm:h-7 sm:w-7',
          )}
        >
          {isHorizontal && rank !== null && '#'}
          {rank ?? '–'}
        </div>
      </>
    )

    if (pillHref) {
      return (
        <Link href={pillHref} onClick={onClick} className={pillClassName}>
          {content}
        </Link>
      )
    }

    return (
      <button type="button" onClick={onClick} className={pillClassName}>
        {content}
      </button>
    )
  }

  // No rank change, or no concrete rank to show a trend against: a plain
  // activation pill, no overlay/sheet on any viewport.
  if (!rankChange || rank === null) return renderPill({ onClick: onClickAction, href })

  // Responsive affordance chosen by CSS, not a JS breakpoint read: render both and
  // toggle visibility. `contents`/`sm:contents` makes the wrapper box disappear so
  // the visible branch stays a direct flex item of the row — layout matches the
  // original single-affordance render exactly, with no hydration flip.
  return (
    <>
      {/* < sm: touch has no hover, so tapping opens the rank-change detail in a Sheet.
          The pill's own tap target stays the sheet trigger either way - when href is
          also set, the sheet gets its own link row so the category is still reachable
          on mobile (desktop's pill IS that link, since hover covers the detail there). */}
      <div className="contents sm:hidden">
        <Sheet
          trigger={renderPill({ onClick: () => setShowSheet(true) })}
          isVisible={showSheet}
          isMenu={true}
          close={() => setShowSheet(false)}
          className="dark"
        >
          <RankOverview rank={rank} rankChange={rankChange} />
          {href && (
            <Link
              href={href}
              onClick={() => setShowSheet(false)}
              className="flex h-11 items-center justify-between border-t border-zinc-800 bg-zinc-950 pl-3 pr-5 text-14 font-medium text-white"
            >
              View category ranking
              <Icon name="chevron-left" size={12} className="rotate-180 text-gray-500" />
            </Link>
          )}
        </Sheet>
      </div>

      {/* >= sm: hover reveals the deferred overview; clicking the pill activates the row. */}
      <div className="hidden sm:contents">
        <DeferredRankOverview rank={rank} rankChange={rankChange} orientation={orientation}>
          {renderPill({ onClick: onClickAction, href })}
        </DeferredRankOverview>
      </div>
    </>
  )
}

function DeferredRankOverview({
  children,
  rank,
  rankChange,
  orientation,
}: {
  children: ReactNode
  rank: number
  rankChange: RankChange
  orientation: 'vertical' | 'horizontal'
}) {
  const isHorizontal = orientation === 'horizontal'

  // Keep the visible rank delta CSS-only on initial load. The hover overview does
  // layout/portal work, so defer it until after real user interaction for Lighthouse.
  const { ref, active } = useDeferredActivation<HTMLDivElement>({
    strategy: 'after-interaction-idle',
    mediaQuery: '(min-width: 640px)',
  })
  const [isXl, setIsXl] = useState(false)

  useEffect(() => {
    if (!active) return

    // Subscribed only after deferred activation, so it stays out of Lighthouse.
    const xl = window.matchMedia('(min-width: 1280px)')
    function update(): void {
      setIsXl(xl.matches)
    }
    update()
    xl.addEventListener('change', update)
    return () => xl.removeEventListener('change', update)
  }, [active])

  const horizontalAlign = isHorizontal ? (isXl ? 'center' : 'left') : isXl ? 'left' : 'right'
  const verticalAlign = isHorizontal && isXl ? 'top' : 'center'

  if (!active) {
    return (
      <div ref={ref} className="w-fit">
        {children}
      </div>
    )
  }

  return (
    <div ref={ref} className="w-fit">
      <Overview
        content={<RankOverview rank={rank} rankChange={rankChange} />}
        horizontalAlign={horizontalAlign}
        verticalAlign={verticalAlign}
      >
        {children}
      </Overview>
    </div>
  )
}

export function RankOverview({ rank, rankChange }: { rank: number; rankChange: RankChange }) {
  const isUp = isRankUp(rank, rankChange)

  const changeDate = new Date(rankChange.updatedAt)
  const isDateValid = !Number.isNaN(changeDate.getTime())
  const ageMs = Date.now() - changeDate.getTime()
  // Exclude future-dated changes (e.g. an editor-set override date) from "recent".
  const isRecent =
    isDateValid && ageMs >= 0 && ageMs <= RANK_DELTA_RECENT_DAYS * 24 * 60 * 60 * 1000

  // A concrete, recent change shows the from→to magnitude and reads "Rank up/down"
  // with a "Changed:" date. Once it's stale (older than RANK_DELTA_RECENT_DAYS) or
  // has no magnitude (positions left blank), it reads "Trending up/down" with a
  // "Since:" date — the date stays visible either way to preserve the freshness signal.
  const showConcreteChange = typeof rankChange.previous === 'number' && isRecent

  return (
    <div className="flex h-[60px] items-center gap-3 whitespace-nowrap bg-zinc-950 pl-3 pr-5 sm:w-max sm:rounded-lg sm:border sm:border-zinc-800">
      {showConcreteChange && (
        <div className="flex h-7 w-fit shrink-0 items-center gap-1 rounded-full border border-zinc-800 bg-zinc-900 px-2.5 font-mono text-14 text-white">
          <span>{rankChange.previous}</span>
          <Icon
            name="chevron-left"
            size={12}
            className={cn('rotate-180', isUp ? 'text-green-500' : 'text-red-500')}
          />
          <span>{rank}</span>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className="text-14 font-semibold leading-none text-white">
          {showConcreteChange ? 'Rank' : 'Trending'} {isUp ? 'up' : 'down'}
        </span>
        {isDateValid && (
          <p className="text-13 leading-none text-gray-500">
            {showConcreteChange
              ? `Changed: ${changeDate.toLocaleDateString('en-US', {
                  timeZone: 'UTC',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}`
              : `Since: ${changeDate.toLocaleDateString('en-US', {
                  timeZone: 'UTC',
                  month: 'short',
                  year: 'numeric',
                })}`}
          </p>
        )}
      </div>
    </div>
  )
}
